import {
  afterNextRender,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  Injector,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import {
  LucideChevronDown,
  LucideChevronUp,
  LucideList,
  LucideLogIn,
  LucideLogOut,
  LucideSettings,
  LucideSun,
  LucideTarget
} from '@lucide/angular';
import { filter } from 'rxjs';
import { AuthService } from './core/auth/auth.service';
import { MeService } from './features/settings/me.service';
import { RunningPillComponent } from './features/timer/running-pill.component';

/**
 * App shell: Today/Focus nav links on the left and an account menu on the right.
 * The right group holds the running-timer pill (LUM-163) before the account menu.
 *
 * @author sadatmalik
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    RunningPillComponent,
    LucideSun,
    LucideTarget,
    LucideChevronDown,
    LucideChevronUp,
    LucideList,
    LucideSettings,
    LucideLogOut,
    LucideLogIn
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly meService = inject(MeService);
  private readonly injector = inject(Injector);

  private readonly menuAnchor = viewChild<ElementRef<HTMLElement>>('menuAnchor');
  private readonly avatarBtn = viewChild<ElementRef<HTMLButtonElement>>('avatarBtn');

  readonly menuOpen = signal(false);
  private readonly meUser = signal<{ displayName: string; email: string } | null>(null);

  readonly signedInName = computed(() => {
    if (!this.auth.isAuthenticated()) {
      return null;
    }
    const user = this.meUser();
    if (!user) {
      return null;
    }
    return user.displayName.trim() || user.email;
  });

  readonly userInitial = computed(() => {
    const name = this.signedInName();
    // Empty until the profile loads, so the avatar never flashes a placeholder letter.
    return name ? name.charAt(0).toUpperCase() : '';
  });

  constructor() {
    effect(() => {
      if (!this.auth.isAuthenticated()) {
        this.meUser.set(null);
        return;
      }
      untracked(() => {
        this.meService.getMe().subscribe({
          next: (me) => this.meUser.set({
            displayName: me.user.displayName,
            email: me.user.email
          }),
          error: () => this.meUser.set(null)
        });
      });
    });

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe(() => this.closeMenu(false));
  }

  toggleMenu(): void {
    if (this.menuOpen()) {
      this.closeMenu();
      return;
    }
    this.menuOpen.set(true);
    afterNextRender(() => this.focusMenuItem(0), { injector: this.injector });
  }

  closeMenu(restoreFocus = true): void {
    if (!this.menuOpen()) {
      return;
    }
    this.menuOpen.set(false);
    if (restoreFocus) {
      this.avatarBtn()?.nativeElement.focus();
    }
  }

  signOut(): void {
    this.closeMenu(false);
    this.auth.logout().subscribe({
      next: () => {
        void this.router.navigate(['/login']);
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen()) {
      return;
    }
    const anchor = this.menuAnchor()?.nativeElement;
    if (anchor && !anchor.contains(event.target as Node)) {
      this.closeMenu();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.menuOpen()) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu();
      return;
    }
    if (event.key === 'Tab') {
      this.closeMenu(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveMenuFocus(event.key === 'ArrowDown' ? 1 : -1);
    }
  }

  private menuItems(): HTMLElement[] {
    const root = this.menuAnchor()?.nativeElement;
    if (!root) {
      return [];
    }
    return Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]'));
  }

  private focusMenuItem(index: number): void {
    const items = this.menuItems();
    items[index]?.focus();
  }

  private moveMenuFocus(delta: 1 | -1): void {
    const items = this.menuItems();
    if (items.length === 0) {
      return;
    }
    const currentIndex = items.findIndex((item) => item === document.activeElement);
    const nextIndex = currentIndex === -1
      ? (delta === 1 ? 0 : items.length - 1)
      : (currentIndex + delta + items.length) % items.length;
    items[nextIndex].focus();
  }
}
